import React, { useState, useRef, useContext, useImperativeHandle, forwardRef } from 'react';
import mapboxgl from 'mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import geojsonExtent from '@mapbox/geojson-extent'; // Import the library
import PropTypes from 'prop-types';
import './ZoomTo.css';
import { ModalContext } from '../../context/ModalContext.js';
import MCLinks from '../../geojson/community_profiles_links.json';


const ZoomTo = forwardRef(({ map, counties, ctus, isOpen, toggleOpen, mapClickPopupRef }, ref) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('city');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const currentPopupRef = useRef(null);
  const { openModal } = useContext(ModalContext);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    clearZoomToPopup: () => {
      if (currentPopupRef.current) {
        currentPopupRef.current.remove();
        currentPopupRef.current = null;
      }
    }
  }));

  // Data for counties shown on the website
  const countyOptions = [
    'ANOKA', 'CARVER', 'DAKOTA', 'HENNEPIN', 'RAMSEY', 'SCOTT', 'WASHINGTON'
  ];
  
  // Function to format county names for display
  const formatCountyName = (name) => {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // This would come from your city data
  const cityOptions = [
    'Afton', 'Andover', 'Anoka', 'Apple Valley', 'Arden Hills', 'Bayport', 'Baytown Twp.', 'Belle Plaine', 
    'Belle Plaine Twp.', 'Benton Twp.', 'Bethel', 'Birchwood Village', 'Blaine', 'Blakeley Twp.', 'Bloomington', 
    'Brooklyn Center', 'Brooklyn Park', 'Burnsville', 'Camden Twp.', 'Carver', 'Castle Rock Twp.', 'Cedar Lake Twp.', 
    'Centerville', 'Champlin', 'Chanhassen', 'Chaska', 'Circle Pines', 'Coates', 'Cologne', 'Columbia Heights', 
    'Columbus', 'Coon Rapids', 'Corcoran', 'Cottage Grove', 'Credit River Twp.', 'Crystal', 'Dahlgren Twp.', 
    'Dayton', 'Deephaven', 'Dellwood', 'Denmark Twp.', 'Douglas Twp.', 'Eagan', 'East Bethel', 'Eden Prairie', 
    'Edina', 'Elko New Market', 'Empire Twp.', 'Eureka Twp.', 'Excelsior', 'Falcon Heights', 'Farmington', 
    'Forest Lake', 'Fort Snelling (unorg.)', 'Fridley', 'Gem Lake', 'Golden Valley', 'Grant', 'Greenfield', 
    'Greenvale Twp.', 'Greenwood', 'Grey Cloud Island Twp.', 'Ham Lake', 'Hamburg', 'Hampton', 'Hampton Twp.', 
    'Hancock Twp.', 'Hanover', 'Hastings', 'Helena Twp.', 'Hilltop', 'Hollywood Twp.', 'Hopkins', 'Hugo', 
    'Independence', 'Inver Grove Heights', 'Jackson Twp.', 'Jordan', 'Lake Elmo', 'Lake St. Croix Beach', 
    'Lakeland', 'Lakeland Shores', 'Laketown Twp.', 'Lakeville', 'Landfall', 'Lauderdale', 'Lexington', 
    'Lilydale', 'Lino Lakes', 'Linwood Twp.', 'Little Canada', 'Long Lake', 'Loretto', 'Louisville Twp.', 
    'Mahtomedi', 'Maple Grove', 'Maple Plain', 'Maplewood', 'Marine on St. Croix', 'Marshan Twp.', 'May Twp.', 
    'Mayer', 'Medicine Lake', 'Medina', 'Mendota', 'Mendota Heights', 'Miesville', 'Minneapolis', 'Minnetonka', 
    'Minnetonka Beach', 'Minnetrista', 'Mound', 'Mounds View', 'New Brighton', 'New Germany', 'New Hope', 
    'New Market Twp.', 'New Prague', 'New Trier', 'Newport', 'Nininger Twp.', 'North Oaks', 'North St. Paul', 
    'Northfield', 'Norwood Young America', 'Nowthen', 'Oak Grove', 'Oak Park Heights', 'Oakdale', 'Orono', 
    'Osseo', 'Pine Springs', 'Plymouth', 'Prior Lake', 'Ramsey', 'Randolph', 'Randolph Twp.', 'Ravenna Twp.', 
    'Richfield', 'Robbinsdale', 'Rockford', 'Rogers', 'Rosemount', 'Roseville', 'San Francisco Twp.', 
    'Sand Creek Twp.', 'Savage', 'Scandia', 'Sciota Twp.', 'Shakopee', 'Shoreview', 'Shorewood', 'South St. Paul', 
    'Spring Lake Park', 'Spring Lake Twp.', 'Spring Park', 'St. Anthony', 'St. Bonifacius', 'St. Francis', 
    'St. Lawrence Twp.', 'St. Louis Park', 'St. Marys Point', 'St. Paul', 'St. Paul Park', 'Stillwater', 
    'Stillwater Twp.', 'Sunfish Lake', 'Tonka Bay', 'Vadnais Heights', 'Vermillion', 'Vermillion Twp.', 
    'Victoria', 'Waconia', 'Waconia Twp.', 'Waterford Twp.', 'Watertown', 'Watertown Twp.', 'Wayzata', 
    'West Lakeland Twp.', 'West St. Paul', 'White Bear Lake', 'White Bear Twp.', 'Willernie', 'Woodbury', 
    'Woodland', 'Young America Twp.'
  ];

  // Add the normalize function at the top of the component
  const normalizeEntityName = (name) => {
    // Convert "Twp." to "Township"
    return name.replace(/\bTwp\./g, 'Township');
  };

  const toggleDropdown = () => {
    if (toggleOpen) {
      toggleOpen();
    }
  };

  const selectCategory = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null); // Close if clicking the same category
    } else {
      setSelectedCategory(category); // Open if clicking a different category
    }
  };

  // Helper function to create popups with smart positioning (same as in map.js)
  const createSmartPopup = (coordinates, htmlContent, estimatedHeight = 200) => {
    if (!map) return null;
    
    // Get current banner height
    const bannerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--banner-height') || '120');
    
    // Convert coordinates to screen pixels
    const screenPoint = map.project(coordinates);
    
    // Get map container dimensions
    const mapContainer = map.getContainer();
    const screenWidth = mapContainer.offsetWidth;
    const screenHeight = mapContainer.offsetHeight;
    
    // Calculate potential collision areas
    const popupWidth = 250; // Max width from CSS (updated)
    const buffer = 20;
    
    // Check various collision scenarios
    const wouldHitTop = screenPoint.y - estimatedHeight < bannerHeight + buffer;
    const wouldHitBottom = screenPoint.y + estimatedHeight > screenHeight - buffer;
    const wouldHitLeft = screenPoint.x - popupWidth/2 < buffer;
    const wouldHitRight = screenPoint.x + popupWidth/2 > screenWidth - buffer;
    
    // Determine optimal anchor and offset
    let anchor = 'bottom';
    let offset = [0, -10];
    
    // Vertical positioning logic
    if (wouldHitTop && !wouldHitBottom) {
      // Show popup below the point
      anchor = 'top';
      offset = [0, 10];
    } else if (wouldHitBottom && !wouldHitTop) {
      // Show popup above the point
      anchor = 'bottom';
      offset = [0, -10];
    } else if (wouldHitTop && wouldHitBottom) {
      // If both top and bottom would be cut off, center it and allow scrolling
      anchor = 'center';
      offset = [0, 0];
    }
    
    // Horizontal positioning adjustments
    if (wouldHitLeft && !wouldHitRight) {
      // Shift popup to the right
      if (anchor === 'bottom') anchor = 'bottom-left';
      else if (anchor === 'top') anchor = 'top-left';
      else if (anchor === 'center') anchor = 'left';
    } else if (wouldHitRight && !wouldHitLeft) {
      // Shift popup to the left
      if (anchor === 'bottom') anchor = 'bottom-right';
      else if (anchor === 'top') anchor = 'top-right';
      else if (anchor === 'center') anchor = 'right';
    }
    
    return new mapboxgl.Popup({ 
      closeButton: true,
      closeOnClick: false,
      anchor: anchor,
      offset: offset,
      maxWidth: '250px' // Ensure consistent width
    })
    .setLngLat(coordinates)
    .setHTML(htmlContent);
  };

  const zoomToLocation = (location, type) => {
    if (!map) return;
    
    try {
      // Clear any existing map-click popup
      if (mapClickPopupRef?.current) {
        mapClickPopupRef.current.remove();
        mapClickPopupRef.current = null;
      }

      // Remove any existing ZoomTo popup before processing any location selection
      if (currentPopupRef.current) {
        currentPopupRef.current.remove();
        currentPopupRef.current = null;
      }

      // Reset previous highlights
      map.setLayoutProperty('ctus-darker', 'visibility', 'none');
      map.setLayoutProperty('counties', 'visibility', 'none');
      map.setFilter('ctus-darker', null);
      map.setFilter('counties', null);

      let featureToZoom = null;

      if (type === 'county') {
        featureToZoom = counties?.features.find(
          feature => feature.properties?.geog_type === 'County' && 
                     feature.properties?.CO_NAME === location
        );
        console.log("ZoomTo: Found countyFeature:", featureToZoom);
        
        // Set county highlight
        if (featureToZoom) {
          map.setLayoutProperty('counties', 'visibility', 'visible');
          map.setFilter('counties', ['==', 'CO_NAME', location]);
        }
        
      } else if (type === 'city') {
        featureToZoom = ctus?.features.find(
          feature => feature.properties?.geog_type === 'CTU' && 
                    feature.properties?.CTU_NAME === location
        );
        console.log("ZoomTo: Found cityFeature:", featureToZoom);
        
        // Set city highlight
        if (featureToZoom) {
          map.setLayoutProperty('ctus-darker', 'visibility', 'visible');
          map.setFilter('ctus-darker', ['==', 'CTU_NAME', location]);
        }
      }
      
      if (featureToZoom && featureToZoom.geometry) {
        const bounds = geojsonExtent(featureToZoom.geometry);
        if (bounds) {
          map.fitBounds(bounds, { padding: 50 });
          
          // If it's a city, create and show the policy popup
          if (type === 'city' && featureToZoom.properties) {
            let coordinates;
            
            if (featureToZoom.geometry.type === 'Point') {
              coordinates = [featureToZoom.geometry.coordinates[0], featureToZoom.geometry.coordinates[1]];
            } else {
              // Calculate the center of the geometry's bounds
              const bounds = geojsonExtent(featureToZoom.geometry);
              coordinates = [
                (bounds[0] + bounds[2]) / 2, // average of min and max longitude
                (bounds[1] + bounds[3]) / 2  // average of min and max latitude
              ];
            }
              
            const ctu_name = featureToZoom.properties.CTU_NAME;
            const policy_ct = featureToZoom.properties.policy_ct;
            const cat_count = featureToZoom.properties.cat_count;
            let categories = '';
            
            // Build categories string
            for (let i = 1; i <= cat_count; i++) {
              categories += `<br>${featureToZoom.properties['_cat' + i]}`;
            }

            // Estimate popup height based on content
            const basePopupHeight = 160; // Base height for title, footer, buttons etc.
            const heightPerCategory = 18; // Approximate height of a line of text for a category
            const estimatedPopupHeight = basePopupHeight + (cat_count * heightPerCategory);

            // Find matching link from MCLinks
            const normalizedCtuName = normalizeEntityName(ctu_name);
            const linkData = MCLinks.city_township_profiles.find(profile => profile.name === normalizedCtuName) || 
                           MCLinks.county_profiles.find(profile => profile.name === normalizedCtuName);
            const hasLink = linkData && linkData.url;
            
            const cityNameDisplay = `<h3 style="color: #091732">${ctu_name}</h3>`;

            const popupContent = `
              <div class="popup-content">
                ${cityNameDisplay}
                <b>Number of reported policies: </b>${policy_ct}
                ${categories}
                <div class="popup-footer">
                  <button 
                    onclick="document.dispatchEvent(new CustomEvent('openTableView', { detail: { name: '${ctu_name}', type: 'city' }}))" 
                    class="table-view-btn"
                    style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; width: 180px; font-weight: normal;"
                  >Open Policy Table</button>
                  ${hasLink ? 
                    `<a href="${linkData.url}" target="_blank" style="text-decoration: none;">
                      <button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; width: 180px;">
                        Open Community Profile
                      </button>
                    </a>` :
                    `<button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; opacity: 0.5; width: 180px;" disabled>
                      Open Community Profile
                    </button>`
                  }
                </div>
              </div>
            `;

            // Create and show the popup with smart positioning
            const popup = createSmartPopup(coordinates, popupContent, estimatedPopupHeight);
            if (popup) {
              popup.addTo(map);

              // Add event listener for the custom event
              const handleTableViewClick = (event) => {
                openModal('tableView', event.detail);
              };
              document.addEventListener('openTableView', handleTableViewClick);

              // Store the popup reference
              currentPopupRef.current = popup;

              // Remove event listener and reference when the popup is closed
              popup.on('close', () => {
                document.removeEventListener('openTableView', handleTableViewClick);
                currentPopupRef.current = null;
              });
            }
          }
          // If it's a county, create and show the county popup
          else if (type === 'county' && featureToZoom.properties) {
            let coordinates;
            
            // Calculate the center of the geometry's bounds
            const bounds = geojsonExtent(featureToZoom.geometry);
            coordinates = [
              (bounds[0] + bounds[2]) / 2, // average of min and max longitude
              (bounds[1] + bounds[3]) / 2  // average of min and max latitude
            ];
              
            const county_name = featureToZoom.properties.CO_NAME;
            const policy_ct = featureToZoom.properties.policy_ct || 0;
            const cat_count = featureToZoom.properties.cat_count || 0;
            let categories = '';
            
            // Build categories string if categories exist
            for (let i = 1; i <= cat_count; i++) {
              if (featureToZoom.properties['_cat' + i]) {
                categories += `<br>${featureToZoom.properties['_cat' + i]}`;
              }
            }

            // Estimate popup height based on content
            const countyBaseHeight = 160;
            const countyHeightPerCategory = 18;
            const countyPopupHeight = countyBaseHeight + (cat_count * countyHeightPerCategory);

            // Find matching link from MCLinks
            const linkData = MCLinks.county_profiles.find(profile => profile.name === `${formatCountyName(county_name)} County`);
            const hasLink = linkData && linkData.url;

            const countyPopupContent = `
              <div class="popup-content">
                <h3 style="color: #091732">${formatCountyName(county_name)} County</h3>
                <b>Number of county-adopted policies: </b>${policy_ct}
                ${categories}
                <div class="popup-footer">
                  <button 
                    onclick="document.dispatchEvent(new CustomEvent('openTableView', { detail: { name: '${county_name}', type: 'county' }}))" 
                    class="table-view-btn"
                    style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 12px; width: 180px; font-weight: normal;"
                  >Open Policy Table</button>
                  ${hasLink ? 
                    `<a href="${linkData.url}" target="_blank" style="text-decoration: none;">
                      <button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; width: 180px;">
                        Open Community Profile
                      </button>
                    </a>` :
                    `<button style="background-color:#091732; color: white; border: none; padding: 8px 16px; border-radius: 4px; margin-top: 4px; cursor: pointer; font-size: 12px; opacity: 0.5; width: 180px;" disabled>
                      Open Community Profile
                    </button>`
                  }
                </div>
              </div>
            `;

            // Create and show the popup with smart positioning
            const popup = createSmartPopup(coordinates, countyPopupContent, countyPopupHeight);
            if (popup) {
              popup.addTo(map);

              // Add event listener for the custom event
              const handleTableViewClick = (event) => {
                openModal('tableView', event.detail);
              };
              document.addEventListener('openTableView', handleTableViewClick);

              // Store the popup reference
              currentPopupRef.current = popup;

              // Remove event listener and reference when the popup is closed
              popup.on('close', () => {
                document.removeEventListener('openTableView', handleTableViewClick);
                currentPopupRef.current = null;
              });
            }
          }
        } else {
          console.error(`ZoomTo: Could not calculate bounds for ${type} ${location}:`, featureToZoom.geometry);
          // Fallback if bounds are null/undefined from geojsonExtent
          map.flyTo({
            center: [-93.247242, 44.944148],
            zoom: 9
          });
        }
      } else {
        console.warn(`ZoomTo: ${type} ${location} not found or has no geometry.`);
        // Fallback if feature not found
        map.flyTo({
          center: [-93.247242, 44.944148],
          zoom: 9
        });
      }
    } catch (error) {
      console.error(`Error zooming to ${type} ${location}:`, error);
      // Fallback to a simple flyTo on any other error
      map.flyTo({
        center: [-93.247242, 44.944148],
        zoom: 9
      });
    }
  };

  const clearSelection = () => {
    // Clear any existing map-click popup
    if (mapClickPopupRef?.current) {
      mapClickPopupRef.current.remove();
      mapClickPopupRef.current = null;
    }

    // Remove any existing ZoomTo popup when clearing selection
    if (currentPopupRef.current) {
      currentPopupRef.current.remove();
      currentPopupRef.current = null;
    }
    
    // Clear highlights
    if (map) {
      map.setLayoutProperty('ctus-darker', 'visibility', 'none');
      map.setLayoutProperty('counties', 'visibility', 'none');
      map.setFilter('ctus-darker', null);
      map.setFilter('counties', null);
    }
    
    setSelectedCategory(null);
    // Optionally zoom out to show the whole region
    if (map) {
      map.flyTo({
        center: [-93.247242, 44.944148],
        zoom: 8.4
      });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (name, type) => {
    zoomToLocation(name, type);
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const getSuggestions = () => {
    if (!searchTerm) return [];
    
    const searchLower = searchTerm.toLowerCase();
    const suggestions = [];
    
    // Add matching cities
    cityOptions.forEach(city => {
      if (city.toLowerCase().includes(searchLower)) {
        suggestions.push({ name: city, type: 'city' });
      }
    });
    
    // Add matching counties
    countyOptions.forEach(county => {
      if (county.toLowerCase().includes(searchLower)) {
        suggestions.push({ name: county, type: 'county' });
      }
    });
    
    return suggestions.slice(0, 5); // Limit to 5 suggestions
  };

  const filteredCountyOptions = countyOptions.filter(county =>
    !searchTerm || county.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCityOptions = cityOptions.filter(city =>
    !searchTerm || city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="zoom-to-container">      
      {isOpen && (
        <div className="zoom-to-menu">
          <div className="menu-content">
            <div className="search-input-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(true)}
              />
              {showSuggestions && searchTerm && (
                <div className="search-suggestions">
                  {getSuggestions().map(suggestion => (
                    <div
                      key={`${suggestion.type}-${suggestion.name}`}
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion.name, suggestion.type)}
                    >
                      <span>{suggestion.name}</span>
                      <span className="suggestion-type">{suggestion.type}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div 
              className={`category-option ${selectedCategory === 'city' ? 'active' : ''}`}
              onClick={() => selectCategory('city')}
            >
              CITY
            </div>
            
            {selectedCategory === 'city' && (
              <div className="location-options city-options">
                {filteredCityOptions.map(city => (
                  <div 
                    key={city} 
                    className="location-option"
                    onClick={() => zoomToLocation(city, 'city')}
                  >
                    {city}
                  </div>
                ))}
              </div>
            )}

            <div 
              className={`category-option ${selectedCategory === 'county' ? 'active' : ''}`}
              onClick={() => selectCategory('county')}
            >
              COUNTY
            </div>
            
            {selectedCategory === 'county' && (
              <div className="location-options">
                {filteredCountyOptions.map(county => (
                  <div 
                    key={county} 
                    className="location-option"
                    onClick={() => zoomToLocation(county, 'county')}
                  >
                    {formatCountyName(county)}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="clear-selection-container">
            <div className="clear-selection" onClick={clearSelection}>
              Clear Selection
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

ZoomTo.propTypes = {
  map: PropTypes.object,
  counties: PropTypes.object,
  ctus: PropTypes.object,
  isOpen: PropTypes.bool,
  toggleOpen: PropTypes.func,
  mapClickPopupRef: PropTypes.object
};

export default ZoomTo; 